/** Timing for the field: the input window's own timers and a debounce built only on them. */

/** The timer pair of the window the input lives in (a page, an iframe or a test DOM). */
export interface Timers {
	setTimeout(fn: () => void, ms: number): unknown;
	clearTimeout(id: unknown): void;
}

/**
 * Timers off the input's own window, so a field inside an iframe or a test DOM is timed by that
 * window — and stops with it — rather than by whichever global happens to be in scope.
 */
export function viewTimers(input: HTMLInputElement): Timers {
	const view = input.ownerDocument.defaultView;
	if (!view) {
		return {
			setTimeout: (fn, ms) => setTimeout(fn, ms),
			clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
		};
	}
	return {
		setTimeout: (fn, ms) => view.setTimeout(fn, ms),
		clearTimeout: (id) => view.clearTimeout(id as number),
	};
}

/**
 * Trailing-edge debounce that still fires at least every `maxWait` ms while typing goes on. Both
 * limits are timers — no wall-clock reads — so the timing is exactly what `timers` says it is.
 */
export function debounce<A extends unknown[]>(
	fn: (...args: A) => void,
	wait: number,
	maxWait: number,
	timers: Timers,
) {
	let timer: unknown;
	let maxTimer: unknown;
	let args: A | null = null;
	const clear = (): void => {
		if (timer !== undefined) timers.clearTimeout(timer);
		if (maxTimer !== undefined) timers.clearTimeout(maxTimer);
		timer = undefined;
		maxTimer = undefined;
	};
	const fire = (): void => {
		clear();
		const a = args;
		args = null;
		if (a) fn(...a);
	};
	const debounced = (...a: A): void => {
		args = a;
		if (timer !== undefined) timers.clearTimeout(timer);
		timer = timers.setTimeout(fire, wait);
		maxTimer ??= timers.setTimeout(fire, maxWait);
	};
	debounced.cancel = (): void => {
		clear();
		args = null;
	};
	return debounced;
}
